import type { IntegrityEvent } from './focusMonitor'

/** Body of one episode as the server stores it. */
export interface IntegrityPayload {
  id: string
  kind: string
  signals: string[]
  started_at: number
  duration_ms: number | null
  returned: boolean
}

export interface IntegrityReporter {
  /** Offer the current episodes; changed ones are sent after a short quiet period. */
  update(events: IntegrityEvent[]): void
  /** Send what changed now, for example when the participant comes back. */
  flushNow(): Promise<void>
  stop(): void
}

export interface IntegrityReporterDeps {
  send: (events: IntegrityPayload[]) => Promise<unknown>
  delayMs?: number
  setTimeout?: (fn: () => void, ms: number) => unknown
  clearTimeout?: (handle: unknown) => void
}

export function toPayload(event: IntegrityEvent): IntegrityPayload {
  return {
    id: event.id,
    kind: event.kind,
    signals: event.signals,
    started_at: Math.round(event.started_at_server ?? event.started_at),
    duration_ms: event.duration_ms,
    returned: event.returned,
  }
}

const signature = (event: IntegrityEvent): string => `${event.duration_ms ?? 'open'}|${event.returned ? 1 : 0}|${event.signals.length}`

/**
 * Reports episodes of leaving the page to the server. It sends only what changed since the last
 * confirmed send, keeps unsent changes for the next attempt, and never throws: the exam goes on
 * whether or not the evidence arrives.
 */
export function createIntegrityReporter(deps: IntegrityReporterDeps): IntegrityReporter {
  const delay = deps.delayMs ?? 1500
  const schedule = deps.setTimeout ?? ((fn, ms) => window.setTimeout(fn, ms))
  const cancel = deps.clearTimeout ?? ((handle) => window.clearTimeout(handle as number))
  const confirmed = new Map<string, string>()
  let latest: IntegrityEvent[] = []
  let timer: unknown = null
  let inflight = false
  let stopped = false

  async function flush(): Promise<void> {
    if (inflight || stopped) return
    const pending = latest.filter((event) => confirmed.get(event.id) !== signature(event))
    if (!pending.length) return
    inflight = true
    // Take at most one request's worth; the next change or retry sends the rest.
    const batch = pending.slice(0, 50)
    try {
      await deps.send(batch.map(toPayload))
      for (const event of batch) confirmed.set(event.id, signature(event))
    } catch {
      // keep them unconfirmed; the next update or flush retries
    } finally {
      inflight = false
    }
  }

  function arm(): void {
    if (timer !== null || stopped) return
    timer = schedule(() => {
      timer = null
      void flush()
    }, delay)
  }

  return {
    update(events) {
      latest = events
      if (events.some((event) => confirmed.get(event.id) !== signature(event))) arm()
    },
    async flushNow() {
      if (timer !== null) {
        cancel(timer)
        timer = null
      }
      await flush()
    },
    stop() {
      stopped = true
      if (timer !== null) cancel(timer)
      timer = null
    },
  }
}
